import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

export type WorkflowType = 'delivery' | 'pre_sales'

export interface WorkflowTheme {
  // Primary colors
  primary: string
  primaryHover: string
  primaryLight: string

  // Accent colors
  accent: string
  accentLight: string

  // Background gradients
  navBg: string
  cardBg: string
  pageBg: string

  // Border colors
  cardBorder: string
  inputBorder: string

  // Button styles
  buttonBg: string
  buttonHover: string
  buttonText: string

  // Text colors
  navText: string
  headingText: string

  // Status/highlight colors
  highlight: string
  highlightBg: string
}

export interface WorkflowFeatures {
  // Delivery features
  audits: boolean
  remediation: boolean
  wafCaf: boolean

  // Pre-sales features
  sow: boolean
  wbs: boolean
  proposals: boolean
  pricing: boolean
}

export interface WorkflowTerminology {
  task: string
  tasks: string
  project: string
  projects: string
}

export interface WorkflowConfig {
  type: WorkflowType
  label: string
  description: string
  theme: WorkflowTheme
  features: WorkflowFeatures
  terminology: WorkflowTerminology
  templateUseCases: string[]
}

// Delivery workflow - existing blue/slate theme
const deliveryConfig: WorkflowConfig = {
  type: 'delivery',
  label: 'Delivery',
  description: 'WAF/CAF assessments, remediation tasks, and technical delivery',
  theme: {
    primary: 'blue-600',
    primaryHover: 'blue-700',
    primaryLight: 'blue-500',
    accent: 'blue-400',
    accentLight: 'blue-300',
    navBg: 'from-slate-900 to-slate-800',
    cardBg: 'slate-800/50',
    pageBg: 'from-slate-900 via-slate-800 to-slate-900',
    cardBorder: 'blue-500/20',
    inputBorder: 'slate-600',
    buttonBg: 'blue-600',
    buttonHover: 'blue-700',
    buttonText: 'white',
    navText: 'blue-400',
    headingText: 'white',
    highlight: 'blue-400',
    highlightBg: 'blue-500/10',
  },
  features: {
    audits: true,
    remediation: true,
    wafCaf: true,
    sow: false,
    wbs: false,
    proposals: false,
    pricing: false,
  },
  terminology: {
    task: 'Remediation Task',
    tasks: 'Remediation Tasks',
    project: 'Engagement',
    projects: 'Engagements',
  },
  templateUseCases: [
    'caf', 'waf', 'aidw', 'security-assessment', 'maturity-assessment',
    'azure', 'm365', 'paloalto', 'citrix', 'vdi', 'nutanix', 'networking',
    'design', 'migration', 'implementation', 'runbook', 'training',
    'optimization', 'modernization', 'other',
  ],
}

// Pre-sales workflow - brighter emerald/teal theme
const preSalesConfig: WorkflowConfig = {
  type: 'pre_sales',
  label: 'Pre-Sales',
  description: 'SOW creation, WBS, proposals, and pre-sales activities',
  theme: {
    primary: 'emerald-500',
    primaryHover: 'emerald-600',
    primaryLight: 'emerald-400',
    accent: 'teal-400',
    accentLight: 'teal-300',
    navBg: 'from-emerald-900 to-teal-900',
    cardBg: 'emerald-900/30',
    pageBg: 'from-slate-900 via-emerald-950 to-slate-900',
    cardBorder: 'emerald-500/30',
    inputBorder: 'emerald-700',
    buttonBg: 'emerald-500',
    buttonHover: 'emerald-600',
    buttonText: 'white',
    navText: 'emerald-300',
    headingText: 'white',
    highlight: 'emerald-400',
    highlightBg: 'emerald-500/10',
  },
  features: {
    audits: false,
    remediation: false,
    wafCaf: false,
    sow: true,
    wbs: true,
    proposals: true,
    pricing: true,
  },
  terminology: {
    task: 'Deliverable',
    tasks: 'Deliverables',
    project: 'Opportunity',
    projects: 'Opportunities',
  },
  templateUseCases: [
    'sow', 'wbs', 'proposal', 'pricing', 'discovery-summary',
    'managed-services', 'other',
  ],
}

const workflowConfigs: Record<WorkflowType, WorkflowConfig> = {
  delivery: deliveryConfig,
  pre_sales: preSalesConfig,
}

interface WorkflowContextType {
  config: WorkflowConfig
  workflowType: WorkflowType
  setWorkflow: (type: WorkflowType) => void
  isPreSales: boolean
  isDelivery: boolean
}

const WorkflowContext = createContext<WorkflowContextType | null>(null)

export function WorkflowProvider({ children }: { children: ReactNode }) {
  const [workflowType, setWorkflowType] = useState<WorkflowType>(() => {
    // Load from localStorage, default to delivery
    const saved = localStorage.getItem('breezydoc-workflow')
    return (saved === 'pre_sales' ? 'pre_sales' : 'delivery') as WorkflowType
  })

  // Persist workflow selection
  useEffect(() => {
    localStorage.setItem('breezydoc-workflow', workflowType)
  }, [workflowType])

  const setWorkflow = useCallback((type: WorkflowType) => {
    setWorkflowType(type)
  }, [])

  const config = workflowConfigs[workflowType]

  return (
    <WorkflowContext.Provider
      value={{
        config,
        workflowType,
        setWorkflow,
        isPreSales: workflowType === 'pre_sales',
        isDelivery: workflowType === 'delivery',
      }}
    >
      {children}
    </WorkflowContext.Provider>
  )
}

export function useWorkflow() {
  const context = useContext(WorkflowContext)
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider')
  }
  return context
}

// Export configs for direct access if needed
export { workflowConfigs, deliveryConfig, preSalesConfig }
